import React, { useState, useEffect, useCallback } from 'react';
import { Button, Alert, CircularProgress, Box } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { canCreateProperty } from '../../services/selfServiceService';
import PropertyCreationWizard from './PropertyCreationWizard';

const AddPropertyButton = ({ onPropertyCreated, variant = "contained", size = "medium", fullWidth = false }) => {
  const { t } = useTranslation('user');
  const { instance } = useMsal();
  const navigate = useNavigate();
  
  const [canCreate, setCanCreate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [propertyLimit, setPropertyLimit] = useState(null);

  const checkPropertyCreationEligibility = useCallback(async () => {
    try {
      setLoading(true);
      const result = await canCreateProperty(instance);
      setCanCreate(result.canCreate);
      setPropertyLimit(result);
      setError('');
    } catch (err) {
      setError('Failed to check property creation eligibility');
      console.error('Error checking property creation eligibility:', err);
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => {
    checkPropertyCreationEligibility();
  }, [checkPropertyCreationEligibility]);

  const handleOpenWizard = () => {
    setWizardOpen(true);
  };

  const handleCloseWizard = () => {
    setWizardOpen(false);
  };

  const handlePropertyCreated = (result) => {
    // Refresh eligibility after property creation
    checkPropertyCreationEligibility();
    
    // Call parent callback if provided
    if (onPropertyCreated) {
      onPropertyCreated(result);
    }
  };

  const handleContactSupport = () => {
    window.open('https://worldexplore-help.freshdesk.com/support/home', '_blank');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!canCreate && propertyLimit) {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box>
            {t('properties.propertyLimitReached', { 
              current: propertyLimit.currentCount, 
              max: propertyLimit.maxAllowed 
            })}
          </Box>
          <Button 
            size="small" 
            onClick={handleContactSupport}
            sx={{ mt: 1 }}
          >
            {t('properties.contactSupport')}
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Button
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        startIcon={<AddIcon />}
        onClick={handleOpenWizard}
        sx={{ mb: propertyLimit?.currentCount > 0 ? 1 : 0 }}
      >
        {t('properties.addProperty')}
      </Button>
      
      {propertyLimit && propertyLimit.currentCount > 0 && (
        <Box sx={{ fontSize: '0.875rem', color: 'text.secondary', textAlign: 'center' }}>
          {t('properties.propertyCount', { 
            current: propertyLimit.currentCount, 
            max: propertyLimit.maxAllowed 
          })}
        </Box>
      )}

      <PropertyCreationWizard
        open={wizardOpen}
        onClose={handleCloseWizard}
        onSuccess={handlePropertyCreated}
      />
    </>
  );
};

export default AddPropertyButton;
